<template>
  <span class="mini-user">
    <online-indicator class="online-indicator" :online="props.online">
      <img
        v-if="avatar.type === 'url' || avatar.type === 'none'"
        class="inline-avatar"
        :alt="`${displayName}`"
        :src="avatar.url"
      />
      <div v-else-if="avatar.type === 'text'" class="inline-avatar placeholder">
        {{ avatar.text }}
      </div>
      <a
        v-if="props.username"
        class="telegram-link"
        :href="`https://t.me/${props.username}`"
        :title="`@${props.username} on Telegram`"
        target="_blank"
      >
        <span>
          {{ displayName }}
        </span>
      </a>
      <span v-else>
        {{ displayName }}
      </span>
    </online-indicator>
  </span>
</template>

<script setup lang="ts">
import OnlineIndicator, { type OnlineStatus } from '@/components/OnlineIndicator.vue'
import { computed } from 'vue'
import { ponyAvatar } from '@/utils/images.ts'
import { NON_ANON_NULL, type NonAnonNull } from '@/stores/ws.ts'

const props = withDefaults(
  defineProps<{
    online?: OnlineStatus
    photo_url?: string | NonAnonNull | null
    username?: string | null
    fullname?: string | null
    first_name?: string | null
    user_id?: number | null
    fallback?: string
    photoFallback?: string
  }>(),
  {
    online: undefined,
    fallback: 'Anonymous',
    photoFallback: 'Unknown',
  },
)

// Compute a robust display name that handles anonymous/null data
const displayName = computed(() => {
  const full = props.fullname?.trim()
  const first = props.first_name?.trim()
  const username = props.username?.trim()
  if (full && full.trim()) return full.trim()
  if (first) return first.trim()
  if (username) return `@${username}`
  if (typeof props.user_id === 'number') return `User ${props.user_id}`
  // use provided fallback or ultimate hardcoded default
  return props.fallback || 'Anonymous'
})
export type FallbackAvatarType = 'url' | 'text' | 'none'
export type FallbackAvatarComputed = {
  type: FallbackAvatarType
  url?: string
  text?: string
}
const avatar = computed<FallbackAvatarComputed>(() => {
  if (props.photo_url) {
    return { type: 'url', url: props.photo_url }
  }
  if (props.photo_url === NON_ANON_NULL) {
    return { type: 'none', url: ponyAvatar() }
  }
  try {
    new URL(props.photoFallback)
    return { type: 'url', url: props.photoFallback }
  } catch {
    return {
      type: 'text',
      text: props.photoFallback || props.fallback || 'Unknown',
    }
  }
})
</script>

<style scoped lang="scss">
.mini-user {
  --avatar-size: 1.5rem;
  --fallback-avatar-font-size: 0.25em;

  .online-indicator {
    display: flex;
    align-items: center;
  }
  .inline-avatar {
    width: var(--avatar-size);
    height: var(--avatar-size);
    border-radius: 50%;
    vertical-align: middle;
    margin-left: 0.3rem;
    &.placeholder {
      font-size: var(--fallback-avatar-font-size);
      background: var(--color-border);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--color-text);
      font-weight: bold;
      overflow: hidden;
    }
  }
  /* Gray out avatar when online status is hidden (unknown) */
  .status.unknown .inline-avatar {
    filter: grayscale(1);
    opacity: 0.65;
  }
}
</style>
